import type { ThemeTokens } from '@/utils/appThemes';
import type { childPaletteTokens } from '@/utils/theme';

/**
 * WeekTokens — det ENDA färgkontraktet som den gemensamma veckovyn (WeekView)
 * läser. Alla tre personas mappar sina käll-tokens in i denna form:
 *   - teen/adult: getThemeTokens(selectedTheme, isDarkMode)  → themeToWeekTokens
 *   - barn:       theme.palette.child (+ primary/success)     → childToWeekTokens
 *
 * SAMMA nycklar överallt; endast värdena byts per persona. Detta är hur de två
 * tidigare token-systemen (useChildTokens vs getThemeTokens) blir ETT kontrakt
 * utan att barnets faktiska färgvärden ändras (se childToWeekTokens).
 */
export type WeekTokens = {
  /** Sidbakgrund. */
  pageBg: string;
  /** Dagskortets bakgrund. */
  cardBg: string;
  /** Dagskortets ram (icke-"idag"). */
  cardBorder: string;
  /** Primär textfärg. */
  text: string;
  /** Dämpad textfärg (datum, tid, tomma dagar). */
  textSoft: string;
  /** "Idag"-markering: kortram + "Idag"-etikett + framstegsbar. */
  accent: string;
  /** Färg på den fyllda bocken (den bärande completed-signalen). */
  done: string;
  /** Lugn bakgrundston bakom en klar aktivitetsrad. */
  completedBg: string;
  /** Skugga på dagskort. */
  cardShadow: string;
};

/**
 * Teen + adult: mappar det gemensamma tema-tokensystemet (getThemeTokens) till
 * WeekTokens. Bevarar teens nuvarande värden exakt: "idag" = primary, bock/klar
 * = accent, klar-bakgrund = accent @ 10% (1A).
 */
export const themeToWeekTokens = (tk: ThemeTokens, isDarkMode: boolean): WeekTokens => ({
  pageBg: tk.bg,
  cardBg: tk.card,
  cardBorder: tk.border,
  text: tk.text,
  textSoft: tk.textSoft,
  accent: tk.primary,
  done: tk.accent,
  completedBg: `${tk.accent}1A`,
  cardShadow: isDarkMode ? '0 2px 8px rgba(0,0,0,0.35)' : '0 2px 8px rgba(0,0,0,0.06)',
});

/**
 * Barn: mappar prototypens palett (theme.palette.child) till WeekTokens. Värdena
 * tas oförändrade ur samma källa som useChildTokens läser idag, så barnvyns
 * FÄRGER blir byte-identiska efter migreringen till det gemensamma kontraktet:
 *   pageBg/cardBg/cardBorder/text/textSoft/completedBg/cardShadow = palette.child.*
 *   accent = temats primary (samma som "idag"-markeringens primary.main idag)
 *   done   = temats success (grön bock på den redan gröna klar-tonen greenLight)
 */
export const childToWeekTokens = (
  c: childPaletteTokens,
  primaryMain: string,
  successMain: string,
): WeekTokens => ({
  pageBg: c.bg,
  cardBg: c.card,
  cardBorder: c.border,
  text: c.text,
  textSoft: c.textSoft,
  accent: primaryMain,
  done: successMain,
  completedBg: c.greenLight,
  cardShadow: c.shadow,
});
